/**
 * React Query hooks for Portfolio Thesis operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as portfolioThesisApi from '../../../services/api/portfolioThesisApi';
import type { PortfolioThesis } from '../../../services/api/portfolioThesisApi';

/**
 * Hook to fetch all portfolio thesis versions for current user.
 */
export function usePortfolioThesis() {
  return useQuery<PortfolioThesis[]>({
    queryKey: ['portfolioThesis'],
    queryFn: portfolioThesisApi.getPortfolioThesis,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

/**
 * Hook to create a new portfolio thesis.
 * Automatically invalidates the thesis cache on success.
 */
export function useCreatePortfolioThesis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: portfolioThesisApi.createPortfolioThesis,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolioThesis'] });
    },
    onError: (error: Error) => {
      console.error('Failed to create portfolio thesis:', error);
      throw error;
    },
  });
}

/**
 * Hook to update portfolio thesis (creates new version).
 * Automatically invalidates the thesis cache on success.
 */
export function useUpdatePortfolioThesis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      thesisId,
      data,
    }: {
      thesisId: number;
      data: portfolioThesisApi.PortfolioThesisUpdateRequest;
    }) => portfolioThesisApi.updatePortfolioThesis(thesisId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolioThesis'] });
    },
    onError: (error: Error) => {
      console.error('Failed to update portfolio thesis:', error);
      throw error;
    },
  });
}

/**
 * Hook to delete portfolio thesis.
 * Automatically invalidates the thesis cache on success.
 */
export function useDeletePortfolioThesis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: portfolioThesisApi.deletePortfolioThesis,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolioThesis'] });
    },
    onError: (error: Error) => {
      console.error('Failed to delete portfolio thesis:', error);
      throw error;
    },
  });
}
